import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  MenuItem,
  Typography,
  Tooltip,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { GetInventoryTransactions } from "../../../services/InventoryTrackerService";
import CommonLoader from "../../common/CommonLoader";
import { formatDate } from "./StockActionDialog";
import useSortedPaging from "./useSortedPaging";

const TYPES = ["Receive", "Use", "Move Out", "Move In", "Count", "Adjust", "PO Receive"];

const toInputDate = (date) => date.toISOString().split("T")[0];

/**
 * ENB-584: stock movement history. With "variance" set it is the count variance report:
 * only physical counts, showing the system quantity against what was counted.
 */
const InventoryTransactions = ({ variance = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(variance ? "Count" : "");
  const [fromDate, setFromDate] = useState(toInputDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [toDate, setToDate] = useState(toInputDate(new Date()));
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setType(variance ? "Count" : "");
    fetchRows(variance ? "Count" : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variance]);

  const fetchRows = async (selectedType = type) => {
    try {
      setLoading(true);
      const response = await GetInventoryTransactions(selectedType, fromDate, toDate);
      setRows(response?.results || []);
    } catch {
      NotificationManager.error("Error loading inventory history");
    } finally {
      setLoading(false);
    }
  };

  const filtered = rows.filter((row) =>
    [row.productName, row.itemNumber, row.variantName, row.locationName, row.lotNumber, row.usedFor, row.createdBy]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Counts that changed nothing are not variances.
  const visible = variance ? filtered.filter((row) => row.quantity !== 0) : filtered;

  const totalVariance = visible.reduce((sum, row) => sum + row.quantity, 0);

  const columns = variance
    ? [
        { label: "Date", field: "createdAt" },
        { label: "Product", field: "productName" },
        { label: "Item #", field: "itemNumber" },
        { label: "Location", field: "locationName" },
        { label: "Lot", field: "lotNumber" },
        { label: "System Qty", field: "quantityBefore" },
        { label: "Counted Qty", field: "quantityAfter" },
        { label: "Variance", field: "quantity" },
        { label: "Counted By", field: "createdBy" },
        { label: "Notes", field: "notes" },
      ]
    : [
        { label: "Date", field: "createdAt" },
        { label: "Type", field: "transactionType" },
        { label: "Product", field: "productName" },
        { label: "Item #", field: "itemNumber" },
        { label: "Location", field: "locationName" },
        { label: "Lot", field: "lotNumber" },
        { label: "Expiration", field: "expirationDate" },
        { label: "Change", field: "quantity" },
        { label: "After", field: "quantityAfter" },
        { label: "Used For", field: "usedFor" },
        { label: "By", field: "createdBy" },
        { label: "Notes", field: "notes" },
      ];

  // Newest first from the API until a header is clicked.
  const { header, pageRows, pagination } = useSortedPaging(visible, columns, {
    rowsPerPageOptions: [25, 50, 100, 200],
  });

  return (
    <div className="vlp-component-container">
      <span className="title">{variance ? "Count Variance Report" : "Inventory History"} </span>
      <hr />
      {/* A form, like Supply Orders, so Enter in any text or date field runs the search. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          fetchRows();
        }}
        style={{ display: "flex", marginBottom: "16px", alignItems: "center", gap: "12px" }}
      >
        <Tooltip
          title="Search by product, item number, location, lot, used for or user. Press Enter to search"
          arrow
          placement="top"
        >
          <TextField
            label="Search product, item #, location, lot"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "20%" }}
          />
        </Tooltip>
        <Tooltip title="Shows entries on or after this date" arrow placement="top">
          <TextField
            label="From"
            type="date"
            variant="outlined"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toDate || undefined }}
            style={{ width: "11%" }}
          />
        </Tooltip>
        <Tooltip title="Shows entries on or before this date" arrow placement="top">
          <TextField
            label="To"
            type="date"
            variant="outlined"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fromDate || undefined }}
            style={{ width: "11%" }}
          />
        </Tooltip>
        {!variance && (
          <Tooltip title="Filter by type" arrow placement="top">
            <TextField
              select
              label="Type"
              variant="outlined"
              size="small"
              value={type}
              onChange={(e) => setType(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              style={{ width: "10%" }}
            >
              <MenuItem value="">All</MenuItem>
              {TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
        )}
        <Button variant="contained" color="primary" type="submit">
          Search
        </Button>
        {variance && (
          <Typography variant="body2" color={totalVariance < 0 ? "error" : "text.primary"}>
            {visible.length} count(s) with a variance, net {totalVariance > 0 ? "+" : ""}
            {totalVariance} units
          </Typography>
        )}
      </form>

      <Paper style={{ marginRight: "20px" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main }}>
              {header}
            </TableHead>
            <TableBody>
              {pageRows.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{ backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white" }}
                >
                  <TableCell>{new Date(row.createdAt).toLocaleString("en-US")}</TableCell>
                  {!variance && <TableCell>{row.transactionType}</TableCell>}
                  <TableCell>{row.variantName ? `${row.productName} - ${row.variantName}` : row.productName}</TableCell>
                  <TableCell>{row.itemNumber}</TableCell>
                  <TableCell>{row.locationName}</TableCell>
                  <TableCell>{row.lotNumber}</TableCell>
                  {variance ? (
                    <>
                      <TableCell>{row.quantityBefore}</TableCell>
                      <TableCell>{row.quantityAfter}</TableCell>
                      <TableCell style={{ color: row.quantity < 0 ? "red" : "green" }}>
                        {row.quantity > 0 ? "+" : ""}
                        {row.quantity}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{formatDate(row.expirationDate)}</TableCell>
                      <TableCell style={{ color: row.quantity < 0 ? "red" : "green" }}>
                        {row.quantity > 0 ? "+" : ""}
                        {row.quantity}
                      </TableCell>
                      <TableCell>{row.quantityAfter}</TableCell>
                      <TableCell>{row.usedFor || row.poNumber}</TableCell>
                    </>
                  )}
                  <TableCell>{row.createdBy}</TableCell>
                  <TableCell>{row.notes}</TableCell>
                </TableRow>
              ))}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length}>Nothing found for these dates.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination}
      </Paper>
      <CommonLoader loading={loading} />
    </div>
  );
};

export default InventoryTransactions;
